<template>
  <v-form ref="formRef" @submit.prevent="onSubmit">
    <v-container>
      <v-row>
        <v-col cols="12" sm="3">
          <v-text-field
            :rules="[validationRules.required]"
            :disabled="isLoading"
            :loading="isLoading"
            v-model="form.description"
            label="Descrição"
            name="description"
          />
        </v-col>
        <v-col cols="12" sm="3">
          <v-text-field
            :rules="[validationRules.required]"
            :disabled="isLoading"
            :loading="isLoading"
            v-model="form.monthly_price"
            label="Preço mensal"
            name="monthly_price"
          />
        </v-col>
        <v-col cols="12" sm="3">
          <v-text-field
            :rules="[validationRules.required]"
            :disabled="isLoading"
            :loading="isLoading"
            v-model="form.annual_price"
            label="Preço anual"
            name="annual_price"
          />
        </v-col>
      </v-row>
      <v-row v-if="!isLoading">
        <v-col cols="12" class="text-h6"> Módulos </v-col>
        <v-col cols="12" v-for="module in processedModules" :key="module.id">
          <v-row>
            <v-col cols="12">
              <v-card-title class="px-0">
                {{ module.description }}
              </v-card-title>
            </v-col>
            <v-col cols="12" class="py-0">
              <v-row>
                <v-col
                  cols="12"
                  xs="12"
                  sm="6"
                  md="4"
                  lg="2"
                  v-for="resource in module.resources"
                  :key="resource.id"
                >
                  <v-card
                    class="resource px-4 fill-height py-0"
                    :class="{
                      unselected: !resource.enabled,
                      'elevation-3': resource.enabled,
                    }"
                  >
                    <v-row>
                      <v-col cols="12" class="py-0">
                        <v-checkbox
                          :disabled="resource.required"
                          v-model="resource.enabled"
                          :label="resource.description"
                        />
                      </v-col>

                      <v-col
                        cols="12"
                        v-for="quota in resource.quotas"
                        :key="quota.id"
                      >
                        <v-text-field
                          :rules="getQuotaRules(resource, quota)"
                          :disabled="!resource.enabled"
                          :label="quota.description"
                          v-model="quota.value"
                        />
                      </v-col>
                    </v-row>
                  </v-card>
                </v-col>
                <v-col></v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-row v-if="isLoading">
        <template v-for="i in 4" :key="i">
          <v-col cols="12">
            <v-skeleton-loader type="chip" />
          </v-col>
          <v-col
            cols="12"
            xs="12"
            sm="6"
            md="4"
            lg="2"
            v-for="j in 6"
            :key="`${i}${j}`"
          >
            <v-skeleton-loader
              ref="skeleton"
              type="image"
              class="mx-auto"
            ></v-skeleton-loader>
          </v-col>
        </template>
      </v-row>
      <v-row>
        <v-col cols="12" class="text-right">
          <v-btn
            color="primary"
            type="submit"
            :disabled="isLoading"
            :loading="isSubmitting"
          >
            Salvar
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
  </v-form>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { validationRules } from "@/utils/validation";
import { useModulesQuery } from "@/queries/modules/modules";
import {
  usePlanQuery,
  useCreatePlanMutation,
  useUpdatePlanMutation,
} from "@/queries/modules/plans";
import { useSnackbar } from "@/plugins/snackbar";
import { useMetaStore } from "@/stores/meta";

const route = useRoute();
const router = useRouter();
const snackbar = useSnackbar();
const metaStore = useMetaStore();

// Refs
const formRef = ref(null);
const form = ref({
  description: "",
  monthly_price: "",
  annual_price: "",
});
const processedModules = ref([]);

// Computed
const planId = computed(() => {
  return route.params.planId ? Number(route.params.planId) : null;
});

// Set page title
metaStore.setTitle(planId.value ? "Editar Plano" : "Novo Plano");

// TanStack Query - fetch modules
const {
  data: modulesData,
  isLoading: isLoadingModules,
  error: modulesError,
} = useModulesQuery();

// TanStack Query - fetch plan (only if editing)
const {
  data: planData,
  isLoading: isLoadingPlan,
  error: planError,
} = usePlanQuery(planId);

// TanStack Query - mutations
const createPlanMutation = useCreatePlanMutation();
const updatePlanMutation = useUpdatePlanMutation();

// Computed
const isLoading = computed(() => isLoadingModules.value || isLoadingPlan.value);
const isSubmitting = computed(
  () =>
    createPlanMutation.isPending.value || updatePlanMutation.isPending.value,
);

// Watch modules data to process it
watch(
  modulesData,
  (data) => {
    if (data?.items) {
      processedModules.value = data.items.map((module) => ({
        ...module,
        resources:
          module.resources?.map((resource) => ({
            ...resource,
            enabled: resource.required,
            quotas:
              resource.quotas?.map((quota) => ({
                ...quota,
                value: null,
              })) || [],
          })) || [],
      }));
    }
  },
  { immediate: true },
);

// Watch plan data to populate form
watch(
  planData,
  (plan) => {
    if (plan && processedModules.value.length > 0) {
      form.value.description = plan.description;
      form.value.annual_price = plan.annual_price;
      form.value.monthly_price = plan.monthly_price;

      // Update modules with plan resources
      processedModules.value.forEach((module) => {
        module.resources = module.resources.map((resource) => {
          const pr = plan.resources?.find((r) => r.name === resource.name);
          if (!pr) {
            return resource;
          }
          return {
            ...resource,
            enabled: true,
            quotas: resource.quotas.map((quota) => {
              const resourceQuota = pr.quotas?.find(
                (p) => p.name === quota.name,
              );
              return {
                ...quota,
                value: resourceQuota?.value ?? null,
              };
            }),
          };
        });
      });
    }
  },
  { immediate: true },
);

// Watch for errors
watch([modulesError, planError], ([modErr, planErr]) => {
  if (modErr) {
    snackbar.error(
      `Ocorreu um erro ao carregar a lista de modulos: ${modErr.message}`,
    );
  }
  if (planErr) {
    snackbar.error(`Ocorreu um erro ao carregar o plano: ${planErr.message}`);
  }
});

// Methods
const getQuotaRules = (resource, quota) => {
  if (resource.enabled && quota.required) {
    return [validationRules.required];
  }
  return [];
};

const onSubmit = async () => {
  const { valid } = await formRef.value.validate();
  if (!valid) {
    return;
  }

  const resources = processedModules.value
    .flatMap((module) => module.resources)
    .filter((resource) => resource.enabled)
    .map((resource) => ({
      name: resource.name,
      quotas: resource.quotas.map((quota) => ({
        name: quota.name,
        value: quota.value,
      })),
    }));

  try {
    const payload = {
      ...form.value,
      resources,
    };

    if (!planId.value) {
      await createPlanMutation.mutateAsync(payload);
    } else {
      await updatePlanMutation.mutateAsync({
        planId: planId.value,
        data: payload,
      });
    }
    snackbar.info("Informações salvas com sucesso");
    router.push({ name: "plan.index" });
  } catch (err) {
    snackbar.error(err.message);
  }
};
</script>

<style scoped lang="scss">
.resource.unselected {
}
</style>
